import { Fragment, useEffect, useMemo, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { Tabs, TabsList, Tab, TabPanel } from '@/components/tabs';
import { PspFeeProvider } from '@/constant';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { selectUserPermissions } from '@/store/authSlice';
import PriceInput from '@/components/inputControls/PriceInput';
import {
  useGetActivePspFeeConfigQuery,
  useUpdateActivePspFeeConfigMutation,
} from '@/services/PspFeeConfigService';
import NumberInput from '@/components/inputControls/NumberInput';


type FormState = {
  percentage: number | '';
  fixed_fee_eur: number | '';
};

type TabKey = 'stripe' | 'paytrail';

const PspFeeConfigManagement = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const userPermissions = useSelector(selectUserPermissions);

  const canUpdate = useMemo(() => {
    return userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('platform_setting_update')
    );
  }, [userPermissions]);

  const [activeTab, setActiveTab] = useState<TabKey>('stripe');

  const provider =
    activeTab === 'stripe' ? PspFeeProvider.STRIPE : PspFeeProvider.PAYTRAIL;

  const {
    data: config,
    isLoading: isFetching,
    isError,
  } = useGetActivePspFeeConfigQuery({ provider });

  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateActivePspFeeConfigMutation();

  const [formData, setFormData] = useState<FormState>({
    percentage: '',
    fixed_fee_eur: '',
  });

  useEffect(() => {
    if (!config) return;

    const nextPercentage = Number(config.percentage);
    const nextFixed = Number(config.fixed_fee_eur);

    setFormData({
      percentage: Number.isFinite(nextPercentage) ? nextPercentage : '',
      fixed_fee_eur: Number.isFinite(nextFixed) ? nextFixed : '',
    });
  }, [config]);

  const isBusy = isFetching || isUpdating;

  const handlePercentageChange = (value: string) => {
    if (value === '') {
      setFormData((prev) => ({ ...prev, percentage: '' }));
      return;
    }

    const num = Number(value);
    if (Number.isNaN(num)) return;

    const rounded = Math.round(num * 100) / 100;
    setFormData((prev) => ({ ...prev, percentage: rounded }));
  };

  const handleFixedFeeChange = (value: number | undefined) => {
    if (value === undefined) {
      setFormData((prev) => ({ ...prev, fixed_fee_eur: '' }));
      return;
    }

    const rounded = Math.round(value * 100) / 100;
    setFormData((prev) => ({ ...prev, fixed_fee_eur: rounded }));
  };

  const handleSave = async () => {
    if (!canUpdate) return;

    const percentageNum = Number(formData.percentage);
    const fixedNum = Number(formData.fixed_fee_eur);

    if (!Number.isFinite(percentageNum) || percentageNum < 0) {
      enqueueSnackbar('Percentage must be a valid number >= 0', {
        variant: 'error',
      });
      return;
    }

    if (!Number.isFinite(fixedNum) || fixedNum < 0) {
      enqueueSnackbar('Fixed fee must be a valid number >= 0', {
        variant: 'error',
      });
      return;
    }

    try {
      await updateConfig({
        provider,
        percentage: Math.round(percentageNum * 100) / 100,
        fixed_fee_eur: Math.round(fixedNum * 100) / 100,
      }).unwrap();

      enqueueSnackbar('PSP fee configuration saved successfully', {
        variant: 'success',
      });
    } catch (e: unknown) {
      const apiMessage = (e as { data?: { message?: string } })?.data?.message;
      enqueueSnackbar(apiMessage || 'Failed to save PSP fee configuration', {
        variant: 'error',
      });
    }
  };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="PSP Fee Configuration" />
              <ToolbarDescription>
                Configure fee percentage and fixed EUR fee for PSP providers.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleSave}
                disabled={isBusy || !canUpdate}
              >
                {isBusy ? 'Saving...' : 'Save changes'}
              </button>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div className="flex flex-col gap-4">
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab((value as TabKey) || 'stripe')}
            className="w-full"
          >
            <div className="flex flex-col gap-4">
              <TabsList className="flex gap-4">
                <Tab value="stripe" className="text-sm">
                  Stripe
                </Tab>
                <Tab value="paytrail" className="text-sm">
                  Paytrail
                </Tab>
              </TabsList>

              <TabPanel value="stripe">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      id="stripe-percentage-label"
                      htmlFor="stripe-percentage"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Percentage
                    </label>
                    <div className="flex items-center gap-2">
                      <NumberInput
                        id="stripe-percentage"
                        autoComplete="off"
                        className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                        min={0}
                        step={0.01}
                        value={formData.percentage}
                        onChange={(e) => handlePercentageChange(e.target.value)}
                        disabled={isBusy}
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>

                  <div>
                    <label
                      id="stripe-fixed-fee-label"
                      htmlFor="stripe-fixed-fee"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Fixed fee (EUR)
                    </label>
                    <div className="flex items-center gap-2">
                      <PriceInput
                        id="stripe-fixed-fee"
                        value={formData.fixed_fee_eur}
                        onChange={handleFixedFeeChange}
                        disabled={isBusy}
                        inputClassName="h-11 rounded-lg border-gray-300"
                      />
                      <span className="text-sm text-gray-600">€</span>
                    </div>
                  </div>

                  {isError && (
                    <div className="md:col-span-2 text-sm text-red-600">
                      Failed to load configuration.
                    </div>
                  )}
                </div>
              </TabPanel>

              <TabPanel value="paytrail">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      id="paytrail-percentage-label"
                      htmlFor="paytrail-percentage"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Percentage
                    </label>
                    <div className="flex items-center gap-2">
                      <NumberInput
                        id="paytrail-percentage"
                        autoComplete="off"
                        className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                        min={0}
                        step={0.01}
                        value={formData.percentage}
                        onChange={(e) => handlePercentageChange(e.target.value)}
                        disabled={isBusy}
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>

                  <div>
                    <label
                      id="paytrail-fixed-fee-label"
                      htmlFor="paytrail-fixed-fee"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Fixed fee (EUR)
                    </label>
                    <div className="flex items-center gap-2">
                      <PriceInput
                        id="paytrail-fixed-fee"
                        value={formData.fixed_fee_eur}
                        onChange={handleFixedFeeChange}
                        disabled={isBusy}
                        inputClassName="h-11 rounded-lg border-gray-300"
                      />
                      <span className="text-sm text-gray-600">€</span>
                    </div>
                  </div>

                  {isError && (
                    <div className="md:col-span-2 text-sm text-red-600">
                      Failed to load configuration.
                    </div>
                  )}
                </div>
              </TabPanel>
            </div>
          </Tabs>
        </div>
      </Container>
    </Fragment>
  );
};

export { PspFeeConfigManagement };
